import React, {
  useState,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import {
  Loader, captureException, useCreateBillingPortalMutation, useFeatureFlags,
} from '@playbookux/playbook-web-core';
import description from './mail-template';

import { abbreviateName } from '../../../../../core/utils/abbreviate-name';
import Screener from '../../../../components/screener';
import { Title } from '../../../../components/screener/styles';
import Demographics from '../../../../../core/components/demographics';
import { generateICSFile } from '../../../../../core/utils/icalendar';

import {
  Column,
  Table,
  Link,
  Row,
  Dropdown,
  ContainerCenter,
  Modal,
  ContainerContent,
  Body,
  ErrorText,
  Header,
  HeaderCell,
  HeaderLabel,
  TabMenu,
  Container,
  ProfileWrapper,
  ModalLoaderContainer,
} from './styles';

import { TEST_SESSION_BOOKING_STATUS } from '../../../../../core/constants/test-session-status';
import EmploymentInfo from '../../participants/employment-info';
import MeetingModal from './meeting-modal';
import { formatScheduleDate } from '../../../../../core/utils/format-date';

const PLAYBOOK = 'playbook';

const MEETING_URL_NULL_ERROR_MESSAGE = 'There isn’t a call link for this test session';

const DID_NOT_SHOW = 'did_not_show';

const TAB_NAME = {
  profile: 'Profile',
  screenerSurvey: 'Screener Survey',
};

const formatDate = (date, timezone) => date && timezone && formatScheduleDate(date, timezone);

const SessionsTable = (props) => {
  const {
    testers,
    timeZone,
    participantsSource,
    surveys,
    handleModalOpen,
    segmentsCount,
    scope,
    company,
  } = props;

  const {
    featureFlags: { demographics: demographicsFeatureFlag, invisibleObservers = false },
    loading: loadingFeatureFlags,
  } = useFeatureFlags(company.id);

  const [openModal, setOpenModal] = useState(null);

  const [fileLoading, setFileLoading] = useState(false);
  const [icsFile, setIcsFile] = useState(null);
  const [openMeetingModal, setOpenMeetingModal] = useState(null);

  const generateFile = async (start, end, firstName, meetingUrl) => {
    try {
      setIcsFile(null);
      setFileLoading(true);
      const summary = `PlaybookUX Research Session with ${firstName}`;
      const location = meetingUrl;

      const startAt = start;

      const endAt = end;

      const file = await generateICSFile({
        summary,
        description,
        location,
        startAt,
        endAt,
      });
      setIcsFile(file);
      setFileLoading(false);
    } catch (err) {
      console.log('Sessions table catch:', err);
    }
  };

  const {
    createPortal,
  } = useCreateBillingPortalMutation();

  const handleUpgradeCompany = useCallback(() => {
    createPortal(company?.stripeCustomerId)
      .then((response) => {
        if (response) {
          window.open(response);
        }
      })
      .catch((error) => {
        captureException(error);
      });
  }, [createPortal, company]);

  const handleOpenModal = (values) => {
    setOpenModal(values);
  };

  const handleClose = () => {
    setOpenModal(null);
  };

  const handleBookingStatusLabel = (bookingStatus) => {
    const value = Object.entries(TEST_SESSION_BOOKING_STATUS)
      .filter((status) => status[1].value === bookingStatus)[0][1].label;

    return value;
  };

  const testerSorted = useMemo(() => _.orderBy(testers, (v) => v.testSessions
    && moment(_.head(v.testSessions).startAt), ['asc']), [testers]);

  const TAB_ITEMS = [
    {
      menuItem: TAB_NAME.profile,
      render: () => (
        <ProfileWrapper>
          <Demographics
            profile={openModal.user?.profile}
            showTitle={false}
            showEmail
            compact
          />
          <EmploymentInfo
            user={openModal.user}
          />
        </ProfileWrapper>
      ),
    },
    {
      menuItem: TAB_NAME.screenerSurvey,
      render: () => (
        <Screener
          title={`Screener Survey for ${openModal.firstName}`}
          screenerSession={openModal.screenerSession}
          surveys={openModal.surveys}
          segment={openModal.segment}
        />
      ),
    },
  ];

  return (
    <>
      <Table basic="very" textAlign="left">
        <Header>
          <Row>
            <HeaderCell>
              <HeaderLabel>Participant</HeaderLabel>
            </HeaderCell>
            <HeaderCell>
              <HeaderLabel>
                Session date & time
              </HeaderLabel>
            </HeaderCell>
            {participantsSource !== PLAYBOOK && (
              <HeaderCell>
                <HeaderLabel>Email</HeaderLabel>
              </HeaderCell>
            )}
            <HeaderCell>
              <HeaderLabel>Status</HeaderLabel>
            </HeaderCell>
            {scope === 'user' && (
              <HeaderCell>
                <HeaderLabel>Meeting link</HeaderLabel>
              </HeaderCell>
            )}
            {segmentsCount > 1 && (
              <HeaderCell>
                <HeaderLabel>Segment</HeaderLabel>
              </HeaderCell>
            )}
          </Row>
        </Header>
        <Body>
          {testerSorted.map((tes) => {
            const {
              user: {
                email,
                profile,
              },
              testerPayment,
              testSessions,
              screenerSession,
              segment,
            } = tes;

            const { status: paymentStatus = null } = testerPayment || {};
            const { firstName = '', lastName = '' } = profile || {};

            const {
              bookingStatus = '',
              startAt,
              endAt,
              id: testSessionId,
              meetingUrl,
              startUrl,
            } = testSessions.reduce((a, b) => (a.createdAt > b.createdAt ? a : b));

            const hasScreener = screenerSession && surveys;

            return (
              <Row key={tes.id}>
                <Column>
                  {firstName}
                  &nbsp;
                  {abbreviateName(lastName)}
                </Column>

                <Column>
                  {startAt && timeZone
                    && moment(startAt).tz(timeZone).format('MMM DD YYYY h:mm A')}
                </Column>

                {participantsSource !== PLAYBOOK && (
                  <Column>
                    {email}
                  </Column>
                )}

                <Column>
                  {handleBookingStatusLabel(bookingStatus)}
                </Column>
                {scope === 'user' && (
                  <Column>
                    {bookingStatus !== TEST_SESSION_BOOKING_STATUS.didNotShow.value && (
                      <Link onClick={() => setOpenMeetingModal({
                        name: firstName,
                        testSessionId,
                        meetingUrl,
                        startUrl,
                        startAt: formatDate(startAt, timeZone),
                        isJoin: true,
                      })}
                      >
                        Join Meeting
                      </Link>
                    )}

                    {!meetingUrl && <ErrorText>{MEETING_URL_NULL_ERROR_MESSAGE}</ErrorText>}
                  </Column>
                )}

                {segmentsCount > 1 && (
                  <Column>
                    {segment.name}
                  </Column>
                )}

                {/* check if it is not did not show or it is paid */}
                {scope === 'user' && (
                  <Column>
                    {bookingStatus !== DID_NOT_SHOW && (paymentStatus !== 'paid' || hasScreener) && (
                      <ContainerCenter>
                        <Dropdown
                          item
                          text="Actions"
                          icon="caret down"
                          direction="left"
                          onClick={() => generateFile(startAt, endAt, firstName, meetingUrl)}
                        >
                          <Dropdown.Menu>
                            {hasScreener && (
                              <Dropdown.Item onClick={() => {
                                handleOpenModal({
                                  firstName,
                                  lastName,
                                  screenerSession,
                                  surveys,
                                  segment,
                                  user: tes.user,
                                });
                              }}
                              >
                                View screener survey
                              </Dropdown.Item>
                            )}

                            {paymentStatus !== 'paid' && (
                              <Dropdown.Item onClick={() => { handleModalOpen(testSessionId, firstName); }}>
                                Participant did not show
                              </Dropdown.Item>
                            )}
                            <Dropdown.Item
                              href={`data:text/calendar;charset=utf8,${encodeURI(icsFile)}`}
                              as="a"
                              download={`meeting_with_${lastName}.ics`}
                              disabled={fileLoading}
                            >
                              Download calendar invite
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => setOpenMeetingModal({
                              name: firstName,
                              testSessionId,
                              meetingUrl,
                              startUrl,
                              startAt: formatDate(startAt, timeZone),
                              isJoin: false,
                            })}
                            >
                              Copy meeting link
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </ContainerCenter>
                    )}
                  </Column>
                )}
              </Row>
            );
          })}
        </Body>

      </Table>

      <MeetingModal
        values={openMeetingModal}
        isOpen={!!openMeetingModal}
        onClose={() => setOpenMeetingModal(null)}
        hiddenObserver={{
          featureFlag: invisibleObservers,
          onUpdate: handleUpgradeCompany,
        }}
      />
      <Modal
        open={!!openModal}
        onClose={handleClose}
      >
        {loadingFeatureFlags
          ? (
            <ModalLoaderContainer>
              <Loader data-loader="session-table-loader" />
            </ModalLoaderContainer>
          )
          : (
            <ContainerContent>
              {openModal && participantsSource === PLAYBOOK && demographicsFeatureFlag && (
                <>
                  <Title>{`${openModal.firstName}`}</Title>
                  <Container>
                    <TabMenu menu={{ secondary: true }} panes={TAB_ITEMS} defaultActiveIndex={0} />
                  </Container>
                </>
              )}
              {openModal && participantsSource !== PLAYBOOK && demographicsFeatureFlag && (
                <>
                  <Screener
                    title={`Screener Survey for ${openModal.firstName}`}
                    screenerSession={openModal.screenerSession}
                    surveys={openModal.surveys}
                    segment={openModal.segment}
                  />
                </>
              )}
              {openModal && !demographicsFeatureFlag && (
                <Screener
                  title={`Screener Survey for ${openModal.firstName}`}
                  screenerSession={openModal.screenerSession}
                  surveys={openModal.surveys}
                  segment={openModal.segment}
                />
              )}
            </ContainerContent>
          )}
      </Modal>

    </>
  );
};

SessionsTable.propTypes = {
  testers: PropTypes.oneOfType([PropTypes.array]).isRequired,
  surveys: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  timeZone: PropTypes.string.isRequired,
  participantsSource: PropTypes.string.isRequired,
  handleModalOpen: PropTypes.func.isRequired,
  segmentsCount: PropTypes.number.isRequired,
  scope: PropTypes.string.isRequired,
  company: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default SessionsTable;
